import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { useQueryClient } from '@tanstack/react-query';
import { useTrackingState } from 'contexts/TrackingProvider';
import { fancyShareQueryKey } from 'queries/fancyShare/queryKey';
import { useLikeFancyShareMutation } from 'queries/fancyShare/useLikeFancyShareMutation';
import { useUnLikeFancyShareMutation } from 'queries/fancyShare/useUnLikeFancyShareMutation';
import { useCallback, useEffect, useState } from 'react';

export const useFancyShareLike = ({
  fancyShareId,
  isLiked,
  likeCount: prevLikeCount,
}: {
  fancyShareId: number;
  isLiked: boolean;
  likeCount: number;
}) => {
  const [liked, setLiked] = useState(isLiked);
  const [likeCount, setLikeCount] = useState(prevLikeCount);
  const { tracker } = useTrackingState();
  const queryClient = useQueryClient();

  const likeFancyShare = useCallback(() => {
    setLiked(true);
    setLikeCount(prev => prev + 1);
  }, []);

  const unlikeFancyShare = useCallback(() => {
    setLiked(false);
    setLikeCount(prev => prev - 1);
  }, []);

  const { mutate: mutateLike } = useLikeFancyShareMutation(fancyShareId, {
    onMutate: () => likeFancyShare(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fancyShareQueryKey.fancyShareLikes }).then();
      tracker.track(TrackingEventType.LikeFancyShare, { fancyShareId });
    },
    onError: () => unlikeFancyShare(),
  });
  const { mutate: mutateUnlike } = useUnLikeFancyShareMutation(fancyShareId, {
    onMutate: () => unlikeFancyShare(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fancyShareQueryKey.fancyShareLikes }).then();
      tracker.track(TrackingEventType.UnlikeFancyShare, { fancyShareId });
    },
    onError: () => likeFancyShare(),
  });

  useEffect(() => {
    setLiked(isLiked);
  }, [isLiked]);

  useEffect(() => {
    setLikeCount(prevLikeCount);
  }, [prevLikeCount]);

  const toggleLike = useCallback(() => {
    if (liked) {
      mutateUnlike();
      return;
    }
    mutateLike();
  }, [liked, mutateLike, mutateUnlike]);

  return {
    liked,
    likeCount,
    toggleLike,
  };
};
